import React, { useCallback, useState, Fragment } from 'react';
import { oneOf, string, number, bool, arrayOf, shape, func } from 'prop-types';
import classnames from 'classnames';
import {
    productShape,
    userShape,
    platformShape,
    cdnShape,
    currencyShape,
    localeShape,
} from 'shapes';
import { Card } from 'components';
import { getProductBanner, category as categoryUtils } from 'utils';
import {
    Merchandizing,
    ImageBlock,
    IMAGE_BLOCK_MODE,
    DescriptionBlock,
    CharacteristicBlock,
    ActionBlock,
    SpecBlock,
    MoreSpecsButton,
} from './components';
import {
    generateProductUrl,
    showAsList,
    showAsListSpec,
    PRODUCT_CARD_MODE as MODE,
    getIsProProduct,
    getImageBlockMode,
} from './ProductCard.utils';
import './ProductCard.scss';
import './ProductCardOverride.scss';

function ProductCard({
    product,
    className,
    lazyloadImage,
    categoryId,
    experiences,
    mode,
    dataPosition,
    dataSponsored,
    user,
    isSalePeriod,
    platform,
    cdn,
    currency,
    locale,
    hasB2B,
    hasProVAT,
    hasSearchEngine,
    hasBingo,
    onAddToCart,
    withWishlist,
    activeWishlist,
    onWishlistClick,
    onPushToGTM,
    withActionBlock,
    unableRedirectToModels,
    hideSpecs,
    useGenericTitle,
}) {
    const [moreCharacteristics, setMoreCharacteristics] = useState(false);

    const merchandizingBanner = getProductBanner(
        product,
        categoryId,
        hasSearchEngine,
        dataSponsored.status,
    );

    const isProProduct = getIsProProduct(product, hasB2B);

    const isProSecondaryCharacteristic =
        product.market === 'b2c' && product.banner.default === 'b2b' && hasB2B;

    const showListSpec = showAsListSpec(mode, product.top_attributes);
    const rootClassnames = classnames('product-card-root', className, {
        [`product-card-root--${merchandizingBanner}`]: merchandizingBanner,
        'product-card-root--list': showAsList(mode, product.top_attributes),
        'product-card-root--list-spec': showListSpec,
        'product-card-root--b2b': isProProduct,
        'product-card-root--show-more-characteristics': moreCharacteristics,
        'product-card-root--no-cta': !withActionBlock,
    });

    const imageBlockMode = getImageBlockMode(mode, IMAGE_BLOCK_MODE);

    const toggleMoreCharacteristics = event => {
        event.preventDefault();
        event.stopPropagation();
        setMoreCharacteristics(!moreCharacteristics);
    };

    const sendToGTM = useCallback(() => {
        if (onPushToGTM) {
            onPushToGTM({
                article_id: product.article_id,
                event: 'click_on_product_card',
                event_trigger: 'product listing',
                is_b2b: product.model_markets?.includes('b2b'),
                is_mf: product.is_mmf,
                is_productoftheweek: merchandizingBanner === 'productOfTheWeek',
                is_recommended: merchandizingBanner === 'recommended',
                is_topsale: merchandizingBanner === 'topSales',
                is_sponsored: dataSponsored.status,
                mabaya_ad_info: dataSponsored.id,
                model_id: product.model_id,
                product_card_position_absolute: dataPosition,
                product_id_algolia: product.objectID,
                product_price: product.price,
            });
        }
    }, [product, merchandizingBanner, dataSponsored, dataPosition]);

    return (
        <Card className={rootClassnames}>
            {merchandizingBanner && (
                <Merchandizing
                    type={merchandizingBanner}
                    withTooltip={platform.IS_PLATFORM_FR}
                />
            )}
            <a
                href={`${generateProductUrl(product)}`}
                title={product.title}
                data-algolia-position={product.position || 0}
                data-algolia-object-id={product.productId}
                data-listing-position={dataPosition}
                data-article-id={product.id}
                data-model-id={product.modelId}
                data-is-mf={product.is_mmf}
                data-is-b2b={
                    product.model_markets &&
                    product.model_markets.includes('b2b')
                }
                data-product-price={product.price}
                data-business-score={product.score}
                onClick={sendToGTM}
            >
                <ImageBlock
                    className="product-card-root__image-block"
                    lazyload={lazyloadImage}
                    mode={imageBlockMode}
                    mainImage={product.image_fullpath}
                    thumbnails={product.thumbnails}
                    brandName={product.brand_name}
                    brandImage={product.brand_image_fullpath}
                    onSale={isSalePeriod && product.on_sale}
                    cdn={cdn}
                    platform={platform}
                />

                {experiences.includes(categoryUtils.EXPERIENCES.PAINT) &&
                    product.has_sample && (
                        <p className="product-card-model">
                            Testeurs disponibles
                        </p>
                    )}

                <DescriptionBlock
                    className="product-card-root__description-block"
                    product={product}
                    currency={currency.value}
                    locale={locale.value}
                    isB2B={user.isB2B}
                    isForeignSeller={
                        hasProVAT && product.seller_country_id !== 'FR'
                    }
                    showRatings={!user.isB2B}
                    useGenericTitle={useGenericTitle}
                />

                <CharacteristicBlock
                    className="product-card-root__characteristic-block"
                    isB2B={user.isB2B}
                    isProProduct={isProProduct}
                    hasBingo={hasBingo}
                    isProSecondaryCharacteristic={isProSecondaryCharacteristic}
                    isMFProduct={product.is_mmf}
                    hasOneDayDelivery={product.has_1day_delivery}
                    hasFreeDelivery={product.has_free_delivery}
                    /* eslint-disable camelcase */
                    minFee={product.delivery_offers?.min_fee?.as_string}
                    /* eslint-enable camelcase */
                    cdn={cdn}
                    lazyload={lazyloadImage}
                    currency={currency.value}
                    platform={platform}
                />

                {!hideSpecs &&
                    (product.brand_image_fullpath ||
                        product.top_attributes) && (
                        <Fragment>
                            <MoreSpecsButton
                                className="product-card-root__more-characteristics-button"
                                active={moreCharacteristics}
                                onClick={toggleMoreCharacteristics}
                            />
                            <SpecBlock
                                className="product-card-root__spec-block"
                                specs={product.top_attributes}
                            />
                        </Fragment>
                    )}

                {withActionBlock && (
                    <ActionBlock
                        className="product-card-root__action-block"
                        product={product}
                        isB2B={hasB2B && user?.isB2B}
                        showWishListButton={withWishlist}
                        isInWishList={activeWishlist}
                        dataPosition={dataPosition}
                        dataSponsored={dataSponsored}
                        onAddToCartClick={onAddToCart}
                        onWishListClick={onWishlistClick}
                        unableRedirectToModels={unableRedirectToModels}
                    />
                )}
            </a>
        </Card>
    );
}

ProductCard.propTypes = {
    className: string,
    lazyloadImage: bool,
    product: productShape.isRequired,
    categoryId: number,
    experiences: arrayOf(string),
    mode: oneOf([MODE.GRID, MODE.GRID_VISUAL, MODE.LIST_SPEC, MODE.LIST]),
    dataPosition: number,
    dataSponsored: shape({
        status: bool,
        id: string,
    }),
    user: userShape,
    isSalePeriod: bool,
    platform: platformShape.isRequired,
    cdn: cdnShape.isRequired,
    currency: currencyShape.isRequired,
    locale: localeShape.isRequired,
    hasB2B: bool,
    hasProVAT: bool,
    hasSearchEngine: bool,
    hasBingo: bool,
    withWishlist: bool,
    activeWishlist: bool,
    onAddToCart: func,
    onPushToGTM: func,
    onWishlistClick: func,
    withActionBlock: bool,
    unableRedirectToModels: bool,
    hideSpecs: bool,
    useGenericTitle: bool,
};

ProductCard.defaultProps = {
    className: '',
    lazyloadImage: false,
    categoryId: null,
    experiences: [],
    mode: MODE.GRID,
    dataPosition: null,
    dataSponsored: {
        status: false,
        id: undefined,
    },
    user: {},
    isSalePeriod: false,
    hasB2B: false,
    hasProVAT: false,
    hasSearchEngine: false,
    hasBingo: true,
    withWishlist: false,
    activeWishlist: false,
    onAddToCart: null,
    onWishlistClick: null,
    onPushToGTM: null,
    withActionBlock: true,
    unableRedirectToModels: false,
    hideSpecs: false,
    useGenericTitle: false,
};

export default ProductCard;
